import { RefreshCw, X } from 'lucide-react';
import { useVersionCheck } from '@hooks/useVersionCheck';

/**
 * Floating notification banner that appears when a new deployment is detected.
 * Prompts the user to reload for the latest version, or snooze for 5 minutes.
 *
 * Only active in production builds — version.json is generated at build time
 * by the versionJsonPlugin in vite.config.ts.
 */
export const UpdateNotification = () => {
  const { updateAvailable, snooze, reload } = useVersionCheck({
    pollInterval: 120_000, // Check every 2 minutes
    snoozeDuration: 5 * 60_000, // Re-prompt after 5 minutes
  });

  if (!updateAvailable) return null;

  return (
    <div className="fixed bottom-6 right-6 z-[9999] max-w-sm w-full animate-in slide-in-from-bottom-4 fade-in duration-300">
      <div className="bg-white font-sans rounded-lg shadow-2xl border border-gray-200 overflow-hidden">
        {/* Header bar */}
        <div className="bg-gradient-to-r from-primary-600 to-primary-700 px-4 py-2.5 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <RefreshCw className="w-4 h-4 text-white" />
            <span className="text-sm font-semibold text-white">Update Available</span>
          </div>
          <button
            type="button"
            onClick={snooze}
            className="text-white/80 hover:text-white transition-colors"
            aria-label="Dismiss update notification"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="px-4 py-3">
          <p className="text-sm font-semibold text-gray-900">
            Drahft has had some improvements made.
          </p>
          <p className="text-xs text-gray-500 mt-1.5">
            If you&apos;re in the middle of something, save your work now before updating. If you
            choose not to update now, Drahft will prompt you again in 5 minutes.
          </p>
        </div>

        {/* Actions */}
        <div className="px-4 pb-3 flex gap-2">
          <button
            type="button"
            onClick={snooze}
            className="flex-1 px-3 py-2 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={reload}
            className="btn-primary flex-1 gap-1.5 px-3 text-xs shadow-sm"
          >
            <RefreshCw className="w-3.5 h-3.5" />
            Update
          </button>
        </div>
      </div>
    </div>
  );
};
